import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ProductCard from "./ProductCard";
import { FeaturedProductServices } from "../services/FeaturedProductsServices";
import { useLocationPrice } from "../hooks/useLocationPrice";
import api from "../api/api";

const getImageUrl = (product) => {
  if (!product?.image) return "assets/images/thumbs/ProductList/product-img1.png";
  const img = String(product.image).trim();
  if (img.startsWith("http://") || img.startsWith("https://")) return img;
  const backendRoot = (api.defaults.baseURL || "").replace(/\/$/, "").replace(/\/api$/, "");
  if (img.startsWith("storage/")) return `${backendRoot}/${img}`;
  if (img.startsWith("/storage/")) return `${backendRoot}${img}`;
  return `${backendRoot}/storage/${img}`;
};

const ProductListOne = () => {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const resolvePrice = useLocationPrice();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const data = await FeaturedProductServices.getFeaturedProducts(0);
        setProducts(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load featured products", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const topDiscountedCards = useMemo(() => {
    const enriched = products
      .map((p) => {
        const resolved = resolvePrice(p);
        return { raw: p, ...resolved };
      })
      .filter((item) => item.belongsToLocation);

    enriched.sort((a, b) => (b.discountPercent ?? 0) - (a.discountPercent ?? 0));

    return enriched.slice(0, 6).map(({ raw: p, newPrice, oldPrice, discount, locationProductPriceId }) => ({
      id: p.id,
      imageSrc: getImageUrl(p),
      title: p.title,
      category: p.category_name || "Uncategorized",
      oldPrice,
      newPrice,
      discount,
      inStock: true,
      rating: p.avg_rating != null ? Number(p.avg_rating) : null,
      reviewCount: Number(p.review_count ?? 0),
      locationProductPriceId,
    }));
  }, [products, resolvePrice]);

  const shopUrl = searchParams.get("loc")
    ? `/shop?loc=${encodeURIComponent(searchParams.get("loc"))}`
    : "/shop";

  return (
    <section className="product-list pt-80">
      <style>{`
        .product-list-accent {
          display: inline-block;
          width: 36px;
          height: 4px;
          border-radius: 2px;
          background: linear-gradient(90deg, #299e60, #6dcf9a);
          margin-bottom: 10px;
        }
        .product-list-heading-title {
          color: #14532d;
        }
        .product-list-heading-sub {
          color: #3d7a52;
        }
        .product-list-view-all {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 10px 22px;
          border-radius: 100px;
          border: 2px solid #299e60;
          font-size: 13px;
          font-weight: 700;
          color: #299e60;
          text-decoration: none;
          transition: all 0.22s ease;
          letter-spacing: 0.02em;
        }
        .product-list-view-all:hover {
          background: #299e60;
          color: #fff;
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(41,158,96,0.28);
        }
        .product-list-view-all i { transition: transform 0.22s ease; }
        .product-list-view-all:hover i { transform: translateX(3px); }
      `}</style>

      <div className="container container-lg">
        <div className="mb-40">
          <div className="flex-between flex-wrap gap-16 align-items-end">
            <div>
              <span className="product-list-accent" />
              <h3 className="text-heading mb-8 product-list-heading-title">Featured Products</h3>
              <p className="text-gray-600 product-list-heading-sub">Highest discounted picks from our store</p>
            </div>
            <Link to={shopUrl} className="product-list-view-all">
              View All Products
              <i className="ph ph-arrow-right" />
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <p className="text-gray-600">Loading products...</p>
          </div>
        ) : (
          <div className="row gy-4 g-12">
            {topDiscountedCards.map((product) => (
              <ProductCard key={product.id} {...product} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductListOne;